/**
 * YouTubeDownloader Component
 *
 * 5KPlayer-style social video downloader (YouTube, TikTok, Instagram).
 * Paste a URL, pick quality, choose output folder, then download. Shows
 * active and recent downloads with progress bars and status indicators.
 *
 * Desktop-only: rendered only when isLocalMode() returns true.
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import { Download, X, Check, Loader2, FolderOpen, AlertCircle } from 'lucide-react';
import { selectFolder, openVideoFolder, getDownloadsPath } from '../../utils/electronBridge';
import { useTranslation } from '../../hooks/useTranslation';
import { useToast } from '../../hooks/useToast';
import { ToastContainer } from '../Toast';
import { ProgressBar } from '../common/ProgressBar';
import { STORAGE_KEYS } from '../../constants/storage';
import { POLLING_INTERVALS, DEBOUNCE } from '../../constants/timings';
import {
  probeYouTubeInfo,
  startYouTubeDownload,
  getYouTubeDownloadStatus,
  cancelYouTubeDownload,
  type ProbedInfo,
  type DownloadStatus,
  type DownloadJobStatus,
} from './services/youtubeDownloadAPI';

// =============================================================================
// Constants
// =============================================================================

const MAX_RECENT_DOWNLOADS = 10;

// Literal fallback paths a pre-fix build (see git history on this file) used
// to compute and persist into localStorage before the real Downloads folder
// was resolved via Electron IPC. Machines that hit that build even once have
// this exact string stuck in STORAGE_KEYS.YOUTUBE_DOWNLOAD_FOLDER forever —
// app rebuilds/reinstalls don't clear localStorage. Treat these as "unset" so
// the effect below re-resolves the real path instead of trusting the poisoned
// value indefinitely.
const LEGACY_POISONED_OUTPUT_DIRS = new Set(['C:\\Users\\User\\Downloads', '~/Downloads']);

const QUALITY_LABELS: Record<string, string> = {
  best: 'Best available',
  '2160': '4K (2160p)',
  '1440': '2K (1440p)',
  '1080': '1080p (Full HD)',
  '720': '720p (HD)',
  '480': '480p',
  '360': '360p',
  '240': '240p',
  audio_mp3: 'Audio only (MP3)',
};

// Mirrors the backend domain allowlist in
// youtube_curator/download/social_url_matcher.py — keep both in sync.
const SOCIAL_VIDEO_URL_PATTERN =
  /^(https?:\/\/)?([\w-]+\.)*(youtube\.com|youtu\.be|tiktok\.com|instagram\.com)(\/|$)/i;

// =============================================================================
// Types
// =============================================================================

interface DownloadEntry extends DownloadStatus {
  addedAt: number;
}

// =============================================================================
// Helpers
// =============================================================================

function formatDuration(seconds: number): string {
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = Math.floor(seconds % 60);
  if (h > 0) {
    return `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
  }
  return `${m}:${String(s).padStart(2, '0')}`;
}

function formatFileSize(bytes: number): string {
  if (bytes === 0) return '';
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KiB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MiB`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GiB`;
}

function isTerminalStatus(status: DownloadJobStatus): boolean {
  return status === 'completed' || status === 'failed' || status === 'cancelled';
}

function loadRecentDownloads(): DownloadEntry[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEYS.YOUTUBE_RECENT_DOWNLOADS);
    if (!raw) return [];
    return JSON.parse(raw) as DownloadEntry[];
  } catch {
    return [];
  }
}

function saveRecentDownloads(entries: DownloadEntry[]): void {
  try {
    const limited = entries.slice(0, MAX_RECENT_DOWNLOADS);
    localStorage.setItem(STORAGE_KEYS.YOUTUBE_RECENT_DOWNLOADS, JSON.stringify(limited));
  } catch {
    // localStorage quota errors are non-fatal
  }
}

// =============================================================================
// StatusIcon sub-component
// =============================================================================

function StatusIcon({ status }: { status: DownloadJobStatus }): React.ReactElement {
  switch (status) {
    case 'completed':
      return <Check className="h-4 w-4 text-green-400 shrink-0" />;
    case 'failed':
      return <AlertCircle className="h-4 w-4 text-red-400 shrink-0" />;
    case 'cancelled':
      return <X className="h-4 w-4 text-white/40 shrink-0" />;
    default:
      return <Loader2 className="h-4 w-4 text-purple-400 animate-spin shrink-0" />;
  }
}

// =============================================================================
// DownloadItem sub-component
// =============================================================================

interface DownloadItemProps {
  entry: DownloadEntry;
  onCancel: (jobId: string) => void;
  onOpen: (filePath: string) => void;
}

function DownloadItem({ entry, onCancel, onOpen }: DownloadItemProps): React.ReactElement {
  const { t } = useTranslation();
  const isActive = !isTerminalStatus(entry.status);
  const showProgress = isActive || entry.status === 'completed';

  return (
    <div className="bg-black/20 border border-white/10 rounded-lg p-3 sm:p-4 space-y-2">
      <div className="flex items-start gap-2">
        <StatusIcon status={entry.status} />
        <div className="flex-1 min-w-0">
          <p className="text-sm text-white/90 truncate font-medium">
            {entry.title || t('youtube.download.pendingTitle', { defaultValue: 'Downloading...' })}
          </p>
          <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 mt-0.5">
            {entry.quality_used && (
              <span className="text-xs text-white/40">
                {QUALITY_LABELS[entry.quality_used] ?? entry.quality_used}
              </span>
            )}
            {entry.duration_s > 0 && (
              <span className="text-xs text-white/40">{formatDuration(entry.duration_s)}</span>
            )}
            {entry.file_size_bytes > 0 && (
              <span className="text-xs text-white/40">{formatFileSize(entry.file_size_bytes)}</span>
            )}
          </div>
        </div>

        {isActive && (
          <button
            onClick={() => onCancel(entry.job_id)}
            className="min-h-[44px] min-w-[44px] flex items-center justify-center rounded-md hover:bg-white/10 transition-colors text-white/50 hover:text-white/80 shrink-0"
            title={t('youtube.download.cancel', { defaultValue: 'Cancel' })}
          >
            <X className="h-4 w-4" />
          </button>
        )}
      </div>

      {showProgress && (
        <div className="space-y-1">
          <ProgressBar percent={entry.progress_percent} height="h-1.5" />
          <div className="flex justify-between items-center">
            <span className="text-xs text-white/40">{entry.speed || ''}</span>
            <span className="text-xs text-white/40">{Math.round(entry.progress_percent)}%</span>
          </div>
        </div>
      )}

      {entry.status === 'failed' && entry.error && (
        <p className="text-xs text-red-400 break-words">
          {t('youtube.download.errors.failed', {
            defaultValue: 'Download failed: {{message}}',
            message: entry.error,
          })}
        </p>
      )}

      {entry.status === 'completed' && entry.file_path && (
        <div className="flex items-center gap-2 pt-1">
          <span className="text-xs text-white/40 truncate flex-1">{entry.file_path}</span>
          <button
            onClick={() => onOpen(entry.file_path ?? '')}
            className="min-h-[36px] min-w-[36px] flex items-center justify-center rounded-md hover:bg-white/10 transition-colors text-white/50 hover:text-white/80 shrink-0"
            title={t('youtube.download.openFile', { defaultValue: 'Show in folder' })}
          >
            <FolderOpen className="h-3.5 w-3.5" />
          </button>
        </div>
      )}
    </div>
  );
}

// =============================================================================
// Main component
// =============================================================================

export function YouTubeDownloader(): React.ReactElement {
  const { t } = useTranslation();
  const { toasts, showToast, removeToast } = useToast();

  // URL input & probe state
  const [url, setUrl] = useState('');
  const [probedInfo, setProbedInfo] = useState<ProbedInfo | null>(null);
  const [isProbing, setIsProbing] = useState(false);
  const [probeError, setProbeError] = useState<string | null>(null);

  // Quality selection
  const [selectedQuality, setSelectedQuality] = useState('best');

  // Output folder — starts empty and is resolved to the real OS Downloads
  // folder on mount (see effect below) unless the user already saved a
  // custom folder in localStorage.
  const [outputDir, setOutputDir] = useState<string>(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEYS.YOUTUBE_DOWNLOAD_FOLDER) ?? '';
      return LEGACY_POISONED_OUTPUT_DIRS.has(saved) ? '' : saved;
    } catch {
      return '';
    }
  });

  // Resolve the real Downloads folder via Electron IPC. Never guess a path
  // like `<homedir>\Downloads` client-side — the actual Windows username or
  // a redirected (e.g. OneDrive) Downloads folder makes that unreliable and
  // can point at a directory the app has no permission to create.
  useEffect(() => {
    if (outputDir) return;
    void getDownloadsPath().then((path) => {
      if (path) setOutputDir(path);
    });
  }, [outputDir]);

  // Download list
  const [downloads, setDownloads] = useState<DownloadEntry[]>(() => loadRecentDownloads());

  // Debounce ref for URL probe
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Polling interval ref
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Persist output dir
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEYS.YOUTUBE_DOWNLOAD_FOLDER, outputDir);
    } catch {
      // non-fatal
    }
  }, [outputDir]);

  // Persist downloads
  useEffect(() => {
    saveRecentDownloads(downloads);
  }, [downloads]);

  // Set initial quality when probe results arrive
  useEffect(() => {
    if (!probedInfo) return;
    const buckets = probedInfo.available_buckets;
    if (buckets.includes('best')) {
      setSelectedQuality('best');
    } else if (buckets.length > 0) {
      setSelectedQuality(buckets[0]);
    }
  }, [probedInfo]);

  // Debounced probe on URL change
  const handleUrlChange = useCallback((value: string) => {
    setUrl(value);
    setProbedInfo(null);
    setProbeError(null);

    if (debounceRef.current) clearTimeout(debounceRef.current);

    if (!SOCIAL_VIDEO_URL_PATTERN.test(value.trim())) return;

    debounceRef.current = setTimeout(async () => {
      setIsProbing(true);
      setProbeError(null);
      try {
        const info = await probeYouTubeInfo(value.trim());
        setProbedInfo(info);
      } catch (err) {
        const msg = err instanceof Error ? err.message : String(err);
        setProbeError(msg);
      } finally {
        setIsProbing(false);
      }
    }, DEBOUNCE.SLOW + 200); // 600ms total
  }, []);

  // Poll active jobs
  const pollActiveJobs = useCallback(async () => {
    setDownloads((prev) => {
      const active = prev.filter((d) => !isTerminalStatus(d.status));
      if (active.length === 0) return prev;
      return prev; // polling is handled outside of state setter
    });

    const snapshot = downloads.filter((d) => !isTerminalStatus(d.status));
    if (snapshot.length === 0) return;

    const updates = await Promise.allSettled(
      snapshot.map((d) => getYouTubeDownloadStatus(d.job_id))
    );

    setDownloads((prev) => {
      const next = [...prev];
      updates.forEach((result, i) => {
        if (result.status === 'fulfilled') {
          const idx = next.findIndex((d) => d.job_id === snapshot[i].job_id);
          if (idx !== -1) {
            next[idx] = { ...next[idx], ...result.value };
          }
        }
      });
      return next;
    });
  }, [downloads]);

  // Start/stop polling based on active jobs
  useEffect(() => {
    const hasActive = downloads.some((d) => !isTerminalStatus(d.status));

    if (hasActive && !pollRef.current) {
      pollRef.current = setInterval(() => {
        void pollActiveJobs();
      }, POLLING_INTERVALS.FAST);
    }

    if (!hasActive && pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }

    return () => {
      if (pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
    };
  }, [downloads, pollActiveJobs]);

  // Cleanup debounce on unmount
  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, []);

  const handleDownload = useCallback(async () => {
    if (!probedInfo || !url.trim()) return;
    try {
      const { job_id } = await startYouTubeDownload({
        url: url.trim(),
        quality: selectedQuality,
        output_dir: outputDir,
      });

      const newEntry: DownloadEntry = {
        job_id,
        status: 'pending',
        progress_percent: 0,
        speed: '',
        file_path: null,
        title: probedInfo.title,
        duration_s: probedInfo.duration_s,
        file_size_bytes: 0,
        quality_used: selectedQuality,
        error: null,
        addedAt: Date.now(),
      };

      setDownloads((prev) => [newEntry, ...prev].slice(0, MAX_RECENT_DOWNLOADS));
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      setProbeError(msg);
    }
  }, [probedInfo, url, selectedQuality, outputDir]);

  const handleCancel = useCallback(async (jobId: string) => {
    try {
      await cancelYouTubeDownload(jobId);
      setDownloads((prev) =>
        prev.map((d) => (d.job_id === jobId ? { ...d, status: 'cancelled' as const } : d))
      );
    } catch {
      // If cancel fails, the job may already be terminal — next poll will fix state
    }
  }, []);

  const handleOpen = useCallback(
    (filePath: string) => {
      if (!filePath) return;
      void (async () => {
        const opened = await openVideoFolder(filePath);
        if (opened) return;
        // Vite dev mode (no Electron bridge) or IPC failure — fall back to clipboard
        try {
          await navigator.clipboard.writeText(filePath);
          showToast(
            'info',
            t('youtube.download.pathCopied', { defaultValue: 'Path copied to clipboard' }),
            filePath
          );
        } catch {
          showToast(
            'error',
            t('youtube.download.openFileFailed', { defaultValue: 'Could not open file' }),
            filePath
          );
        }
      })();
    },
    [showToast, t]
  );

  return (
    <div className="space-y-6">
      {/* Section A: URL input */}
      <div className="space-y-2">
        <label className="text-sm font-medium text-white/70">
          {t('youtube.download.urlPlaceholder', { defaultValue: 'Paste a video URL here...' })}
        </label>
        <div className="flex flex-col sm:flex-row gap-2">
          <input
            type="url"
            value={url}
            onChange={(e) => handleUrlChange(e.target.value)}
            placeholder={t('youtube.download.urlPlaceholder', {
              defaultValue: 'Paste a video URL here...',
            })}
            className="flex-1 bg-black/20 border border-white/10 rounded-lg px-3 py-2.5 text-sm text-white placeholder-white/30 focus:outline-none focus:border-purple-500/50 focus:ring-1 focus:ring-purple-500/30 min-h-[44px]"
          />
          <button
            onClick={() => {
              if (url.trim() && SOCIAL_VIDEO_URL_PATTERN.test(url.trim())) {
                setIsProbing(true);
                setProbeError(null);
                setProbedInfo(null);
                probeYouTubeInfo(url.trim())
                  .then((info) => setProbedInfo(info))
                  .catch((err) => {
                    setProbeError(err instanceof Error ? err.message : String(err));
                  })
                  .finally(() => setIsProbing(false));
              }
            }}
            disabled={isProbing || !url.trim()}
            className="min-h-[44px] px-4 rounded-lg bg-purple-600/80 hover:bg-purple-600 disabled:opacity-50 disabled:cursor-not-allowed text-white text-sm font-medium transition-colors flex items-center gap-2 justify-center shrink-0"
          >
            {isProbing ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Download className="h-4 w-4" />
            )}
            {t('youtube.download.fetchInfo', { defaultValue: 'Fetch info' })}
          </button>
        </div>

        {probeError && (
          <p className="text-sm text-red-400 flex items-center gap-1.5">
            <AlertCircle className="h-4 w-4 shrink-0" />
            {probeError}
          </p>
        )}
      </div>

      {/* Section B: Video info card */}
      {probedInfo && (
        <div className="bg-black/30 border border-white/10 rounded-xl p-4 flex flex-col sm:flex-row gap-4">
          {probedInfo.thumbnail_url && (
            <img
              src={probedInfo.thumbnail_url}
              alt={probedInfo.title}
              className="w-full sm:w-40 h-24 sm:h-auto object-cover rounded-lg shrink-0"
            />
          )}
          <div className="flex-1 space-y-3 min-w-0">
            <div>
              <p className="text-white font-medium text-sm sm:text-base line-clamp-2">
                {probedInfo.title}
              </p>
              {probedInfo.duration_s > 0 && (
                <p className="text-white/50 text-xs mt-1">
                  {formatDuration(probedInfo.duration_s)}
                </p>
              )}
            </div>

            <div className="space-y-1">
              <label className="text-xs font-medium text-white/60 uppercase tracking-wide">
                {t('youtube.download.quality', { defaultValue: 'Quality' })}
              </label>
              <select
                value={selectedQuality}
                onChange={(e) => setSelectedQuality(e.target.value)}
                className="w-full bg-black/30 border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-purple-500/50 min-h-[44px]"
              >
                {probedInfo.available_buckets.map((bucket) => (
                  <option key={bucket} value={bucket} className="bg-gray-900">
                    {QUALITY_LABELS[bucket] ?? `${bucket}p`}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>
      )}

      {/* Section C: Output folder */}
      <div className="space-y-2">
        <label className="text-sm font-medium text-white/70">
          {t('youtube.download.outputFolder', { defaultValue: 'Save to' })}
        </label>
        <div className="flex flex-col sm:flex-row gap-2">
          <input
            type="text"
            value={outputDir}
            onChange={(e) => setOutputDir(e.target.value)}
            readOnly
            className="flex-1 bg-black/20 border border-white/10 rounded-lg px-3 py-2.5 text-sm text-white/70 min-h-[44px] cursor-default"
          />
          <button
            onClick={() =>
              void selectFolder().then((result) => {
                if (result.status === 'selected') {
                  setOutputDir(result.path);
                } else if (result.status === 'error') {
                  showToast('error', t('common.error'), result.error);
                }
              })
            }
            className="min-h-[44px] px-4 rounded-lg bg-black/30 hover:bg-white/10 border border-white/10 text-white/70 text-sm font-medium transition-colors flex items-center gap-2 justify-center shrink-0"
          >
            <FolderOpen className="h-4 w-4" />
            {t('youtube.download.chooseFolder', { defaultValue: 'Choose folder...' })}
          </button>
        </div>
      </div>

      {/* Section D: Download button */}
      <button
        onClick={() => void handleDownload()}
        disabled={!probedInfo}
        className="w-full min-h-[48px] rounded-xl bg-purple-600 hover:bg-purple-500 disabled:opacity-40 disabled:cursor-not-allowed text-white font-semibold text-base transition-colors flex items-center justify-center gap-2"
      >
        <Download className="h-5 w-5" />
        {t('youtube.download.downloadButton', { defaultValue: 'Download' })}
      </button>

      {/* Recent / active downloads */}
      {downloads.length > 0 && (
        <div className="space-y-2">
          <h3 className="text-sm font-medium text-white/50 uppercase tracking-wide">Downloads</h3>
          <div className="space-y-2">
            {downloads.map((entry) => (
              <DownloadItem
                key={entry.job_id}
                entry={entry}
                onCancel={(jobId) => void handleCancel(jobId)}
                onOpen={handleOpen}
              />
            ))}
          </div>
        </div>
      )}
      <ToastContainer toasts={toasts} onRemove={removeToast} />
    </div>
  );
}
